import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getFieldPosition,
  scrollToInvalidField,
} from 'src/components/organisms/Personalisation/utils';
import { BASKET_PAGE_PATH } from 'src/constants/page';
import {
  selectInvalidCustomOptionValues,
  selectIsInEditMode,
  selectOptions,
  selectOutstandingOptions,
  selectTrackedEvents,
} from 'src/redux/basket/selectors';
import type { ReduxApplicationState } from 'src/redux/types';
import * as basketAPIMutations from 'src/services/basket-api/mutations';
import * as basketAPIQueries from 'src/services/basket-api/queries';
import type {
  BasketAPIBasketQuery,
  BasketAPIBasketWithItemsQuery,
  BasketAPIItem,
  BasketAPIMoney,
  BasketAPIMutationTrackingData,
} from 'src/services/basket-api/types/BasketAPIResponseBody';
import type { ProductOptionInputWithoutLabel } from 'src/services/basket-api/types/graphql';
import type {
  CatServiceAPIProductOption,
  CatServiceAPIProductOptionValue,
} from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import { dispatchBasketUpdatedEvent } from './domEvents';
import type { SelectedOptionValuesRecord, TrackedEvents } from './types';

// Defined locally (mirrors `AppThunkDispatch` in `../store`) to avoid a
// circular import: store → GAMiddleware → basket/eventsMap → basket/thunks.
type AppThunkDispatch = ThunkDispatch<ReduxApplicationState, void, AnyAction>;

export const getBasketForViewing = createAsyncThunk<BasketAPIBasketQuery>(
  'basket/getBasketForViewing',
  async (_, { getState }) => {
    const { user } = getState() as ReduxApplicationState;

    return await basketAPIQueries.getBasket(user.currencyCode);
  },
);

interface PopulateOptionValuesPayload {
  discountedItemsTotal: BasketAPIMoney;
  item: BasketAPIItem | undefined;
}

// Single hydration thunk for edit mode: given the id of the item being edited,
// fetches the basket with item options, resolves that item, and hands both the
// resolved item and the basket totals to the reducer (which persists the id as
// `editedItem` for the later update mutation). The basket items themselves are
// not persisted — they exist only transiently to seed the form.
export const populateOptionValuesFromBasketItem = createAsyncThunk<
  PopulateOptionValuesPayload,
  string
>('basket/populateOptionValuesFromBasketItem', async (editItemId, { getState }) => {
  const { user } = getState() as ReduxApplicationState;
  const { discountedItemsTotal, items } = await basketAPIQueries.getBasketWithItemOptions(
    user.currencyCode,
  );

  return {
    discountedItemsTotal,
    item: (items || []).find(({ id }) => id === editItemId),
  };
});

export const mapProductOptionsToBasketOptions = (
  selectedOptionValues: SelectedOptionValuesRecord,
  productOptions: CatServiceAPIProductOption[],
): ProductOptionInputWithoutLabel => {
  const optionById = new Map(productOptions.map((o) => [o.id, o]));

  return Object.keys(selectedOptionValues).flatMap((optionIdStr) => {
    const optionIdNum = Number(optionIdStr);
    const productOption = optionById.get(optionIdNum);
    const entries = selectedOptionValues[optionIdStr] ?? [];

    return entries.flatMap(
      (value: CatServiceAPIProductOptionValue): ProductOptionInputWithoutLabel => {
        const { id, isCustomInput, name } = value;

        if (!productOption) {
          return [];
        }

        if (productOption.type === 'selection') {
          return [{ optionId: optionIdStr, valueId: String(id) }];
        }

        if (productOption.type === 'image') {
          return [{ optionId: optionIdStr, value: name }];
        }

        return isCustomInput
          ? [{ optionId: optionIdStr, value: name }]
          : [{ optionId: optionIdStr, valueId: String(id) }];
      },
    );
  });
};

interface AddItemToBasketPayload {
  basket: {
    discountedItemsTotal: BasketAPIMoney;
    itemsCount: number;
    itemsTotal: BasketAPIMoney;
  };
  trackingData: BasketAPIMutationTrackingData;
}

export const addItemToBasket = createAsyncThunk<AddItemToBasketPayload>(
  'basket/addItemToBasket',
  async (_, { dispatch, getState }) => {
    const {
      basket: { options: productOptions, quantity, selectedOptionValues },
      product: { code },
      user: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedOptionValues, productOptions);

    const { basket, trackingData } = await basketAPIMutations.addItem(
      code,
      quantity,
      currencyCode,
      options,
    );

    dispatchBasketUpdatedEvent();

    // Dispatched as a plain action (rather than importing the slice's `actions`)
    // to avoid a circular import between slice.ts and thunks.ts.
    dispatch({ type: 'basket/openBasketModal' });

    return {
      basket,
      trackingData,
    };
  },
);

export const updateItemInBasket = createAsyncThunk<void>(
  'basket/updateItemInBasket',
  async (_, { getState }) => {
    const {
      basket: { editedItem, options: productOptions, quantity, selectedOptionValues },
      user: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedOptionValues, productOptions);

    // On success the basket-api response is unused: we navigate straight to the
    // basket page, tearing down this page and store, so there's nothing to
    // surface back into state. `updateItem` is called purely to apply the
    // mutation (and throw on failure, which the rejected reducer handles).
    await basketAPIMutations.updateItem(editedItem!, quantity, currencyCode, options);

    window.location.assign(BASKET_PAGE_PATH);
  },
);

interface AddMembershipItemToBasketPayload {
  basket: BasketAPIBasketWithItemsQuery;
}

export const addMembershipItemToBasket = createAsyncThunk<AddMembershipItemToBasketPayload>(
  'basket/addMembershipItemToBasket',
  async (_, { getState }) => {
    const {
      user: { currencyCode },
    } = getState() as ReduxApplicationState;

    const { basket } = await basketAPIMutations.addMembershipItem(currencyCode);

    dispatchBasketUpdatedEvent();

    return { basket };
  },
);

const trackOutstandingSelections = (
  outstandingSelections: CatServiceAPIProductOption[],
  productOptions: CatServiceAPIProductOption[],
  trackedEvents: TrackedEvents,
  dispatch: AppThunkDispatch,
) => {
  outstandingSelections.forEach((opt) => {
    if (!trackedEvents.error['Add to Cart Error'][opt.name]) {
      const isMultipleOptionSelection = (opt.values ?? []).length > 3;
      const selectionLabel = isMultipleOptionSelection
        ? '3 and more values selection'
        : 'single & binary selection';

      const fieldType =
        opt.type === 'image'
          ? 'image upload'
          : opt.type === 'selection'
          ? selectionLabel
          : 'input field';

      dispatch({
        type: 'basket/trackInteractionError',
        payload: {
          field_action: 'Add to Cart Error',
          field_name: opt.name,
          field_position: getFieldPosition(productOptions, opt),
          field_type: fieldType,
          required_field: opt.required,
        },
      });
    }
  });
};

interface ThunkConfig {
  dispatch: AppThunkDispatch;
  state: ReduxApplicationState;
}

export const submitOptionsForm = createAsyncThunk<void, void, ThunkConfig>(
  'basket/submitOptionsForm',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const outstandingSelections = selectOutstandingOptions(state);
    const invalidCustomSelections = selectInvalidCustomOptionValues(state);
    const isInEditMode = selectIsInEditMode(state);
    const productOptions = selectOptions(state);
    const trackedEvents = selectTrackedEvents(state);
    const hasOptions = productOptions.length > 0;
    const isDrawerOpen = state.basket.isOptionsExpanded;

    // When the product has options, the page CTA opens the options drawer rather
    // than submitting. The in-drawer CTA (drawer already open) falls through to
    // validate and add to basket.
    if (hasOptions && !isDrawerOpen) {
      dispatch({ type: 'basket/setOptionsExpanded', payload: { expanded: true } });
      return;
    }

    dispatch({ type: 'basket/showValidationErrors' });

    if (
      outstandingSelections.length > 0 ||
      invalidCustomSelections.length > 0 ||
      !state.basket.isQuantityValid
    ) {
      dispatch({ type: 'basket/setOptionsExpanded', payload: { expanded: true } });

      trackOutstandingSelections(outstandingSelections, productOptions, trackedEvents, dispatch);

      scrollToInvalidField();
      return;
    }

    const result = isInEditMode
      ? await dispatch(updateItemInBasket())
      : await dispatch(addItemToBasket());

    // Close the drawer only once the item has been successfully added/updated,
    // so a failed request leaves the drawer open for the user to retry.
    if (addItemToBasket.fulfilled.match(result) || updateItemInBasket.fulfilled.match(result)) {
      dispatch({
        type: 'basket/setOptionsExpanded',
        payload: { expanded: false },
      });
    }
  },
);
